import { lazyWithRetry } from "@/lib/lazyWithRetry";
import { Suspense } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { AppLayout } from "@/components/layouts/AppLayout";
import { BusinessSettingsProvider } from "@/contexts/BusinessSettingsContext";
import { AuthProvider } from "@/contexts/AuthContext";
import { NewOrderDialogProvider } from "@/components/admin/orders/NewOrderDialogProvider";
import Index from "./pages/Index";

// Defer non-critical UI (analytics + cookie banner) so they don't block FCP/LCP.
const GtmLoader = lazyWithRetry(() => import("@/components/GtmLoader").then(m => ({ default: m.GtmLoader })));
const CookieConsent = lazyWithRetry(() => import("@/components/CookieConsent").then(m => ({ default: m.CookieConsent })));

// Lazy-loaded routes — keeps the initial JS bundle small so the
// homepage paints fast. Each route loads on demand.
const Materials = lazyWithRetry(() => import("./pages/Materials"));
const MaterialDetail = lazyWithRetry(() => import("./pages/MaterialDetail"));
const Slabs = lazyWithRetry(() => import("./pages/Slabs"));
const SlabDetail = lazyWithRetry(() => import("./pages/SlabDetail"));
const Quote = lazyWithRetry(() => import("./pages/Quote"));
const Book = lazyWithRetry(() => import("./pages/Book"));
const Login = lazyWithRetry(() => import("./pages/Login"));
const CustomerDashboard = lazyWithRetry(() => import("./pages/CustomerDashboard"));
const TrackOrder = lazyWithRetry(() => import("./pages/TrackOrder"));
const FAQ = lazyWithRetry(() => import("./pages/FAQ"));
const MaterialComparison = lazyWithRetry(() => import("./pages/MaterialComparison"));
const Legal = lazyWithRetry(() => import("./pages/Legal"));
const NotFound = lazyWithRetry(() => import("./pages/NotFound"));
const Schedule = lazyWithRetry(() => import("./pages/Schedule"));
const AuthCallback = lazyWithRetry(() => import("./pages/AuthCallback"));
const PaymentSuccess = lazyWithRetry(() => import("./pages/PaymentSuccess"));
const SetPassword = lazyWithRetry(() => import("./pages/SetPassword"));
const ResetPassword = lazyWithRetry(() => import("./pages/ResetPassword"));
const Portfolio = lazyWithRetry(() => import("./pages/Portfolio"));
const ProjectDetail = lazyWithRetry(() => import("./pages/ProjectDetail"));
const AppointmentConfirm = lazyWithRetry(() => import("./pages/AppointmentConfirm"));
const AppointmentReschedule = lazyWithRetry(() => import("./pages/AppointmentReschedule"));
const LocalServicePage = lazyWithRetry(() => import("./pages/LocalServicePage"));
const ReservationSuccess = lazyWithRetry(() => import("./pages/ReservationSuccess"));
const FreeEstimateLanding = lazyWithRetry(() => import("./pages/FreeEstimateLanding"));
const Blog = lazyWithRetry(() => import("./pages/Blog"));
const BlogPost = lazyWithRetry(() => import("./pages/BlogPost"));
const SalesShareList = lazyWithRetry(() => import("./pages/SalesShareList"));


// Admin bundle (separate chunk — never loaded for public visitors)
const AdminLayout = lazyWithRetry(() => import("@/components/layouts/AdminLayout").then(m => ({ default: m.AdminLayout })));
const AdminOverview = lazyWithRetry(() => import("./pages/admin/AdminOverview"));
const AdminSettings = lazyWithRetry(() => import("./pages/admin/AdminSettings"));
const AdminAvailability = lazyWithRetry(() => import("./pages/admin/AdminAvailability"));
const AdminAiInsights = lazyWithRetry(() => import("./pages/admin/AdminAiInsights"));
const AdminInventory = lazyWithRetry(() => import("./pages/admin/AdminInventory"));
const AdminPricing = lazyWithRetry(() => import("./pages/admin/AdminPricing"));
const AdminOrders = lazyWithRetry(() => import("./pages/admin/AdminOrders"));
const AdminOrderDetail = lazyWithRetry(() => import("./pages/admin/AdminOrderDetail"));
const AdminAppointments = lazyWithRetry(() => import("./pages/admin/AdminAppointments"));
const AdminCustomers = lazyWithRetry(() => import("./pages/admin/AdminCustomers"));
const AdminAnalytics = lazyWithRetry(() => import("./pages/admin/AdminAnalytics"));
const AdminLegal = lazyWithRetry(() => import("./pages/admin/AdminLegal"));
const AdminLeads = lazyWithRetry(() => import("./pages/admin/AdminLeads"));
const AdminSeo = lazyWithRetry(() => import("./pages/admin/AdminSeo"));
const AdminSeoIndexing = lazyWithRetry(() => import("./pages/admin/AdminSeoIndexing"));
const AdminSeoGrowth = lazyWithRetry(() => import("./pages/admin/AdminSeoGrowth"));
const AdminBlog = lazyWithRetry(() => import("./pages/admin/AdminBlog"));
const AdminFinancials = lazyWithRetry(() => import("./pages/admin/AdminFinancials"));
const AdminProjects = lazyWithRetry(() => import("./pages/admin/AdminProjects"));
const AdminProfitSettlement = lazyWithRetry(() => import("./pages/admin/AdminProfitSettlement"));
const AdminLayouts = lazyWithRetry(() => import("./pages/admin/AdminLayouts"));
const AdminLayoutDetail = lazyWithRetry(() => import("./pages/admin/AdminLayoutDetail"));
const AdminPermits = lazyWithRetry(() => import("./pages/admin/AdminPermits"));
const AdminAds = lazyWithRetry(() => import("./pages/admin/AdminAds"));
const AdminSalesList = lazyWithRetry(() => import("./pages/admin/AdminSalesList"));


const queryClient = new QueryClient();

const RouteFallback = () => (
  <div className="min-h-[50vh] flex items-center justify-center">
    <div className="h-8 w-8 rounded-full border-2 border-accent border-t-transparent animate-spin" aria-label="Loading" />
  </div>
);

const App = () => (
  <QueryClientProvider client={queryClient}>
    <AuthProvider>
    <BusinessSettingsProvider>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <Suspense fallback={null}><GtmLoader /></Suspense>
      <BrowserRouter>
        <Suspense fallback={<RouteFallback />}>
        <Routes>
          {/* Paid-ads landing — wrapped in AppLayout so visitors can navigate the rest of the site. */}
          <Route path="/free-estimate-sarasota" element={<AppLayout><FreeEstimateLanding /></AppLayout>} />

          {/* Customer routes */}
          <Route path="/" element={<AppLayout><Index /></AppLayout>} />
          <Route path="/materials" element={<AppLayout><Materials /></AppLayout>} />
          <Route path="/materials/:slug" element={<AppLayout><MaterialDetail /></AppLayout>} />
          <Route path="/slabs" element={<AppLayout><Slabs /></AppLayout>} />
          <Route path="/slabs/:category/:slug" element={<AppLayout><SlabDetail /></AppLayout>} />
          <Route path="/slabs/:slug" element={<AppLayout><SlabDetail /></AppLayout>} />
          <Route path="/quote" element={<AppLayout><Quote /></AppLayout>} />
          <Route path="/book" element={<AppLayout><Book /></AppLayout>} />
          <Route path="/schedule/:reservationId" element={<AppLayout><Schedule /></AppLayout>} />
          <Route path="/login" element={<AppLayout><Login /></AppLayout>} />
          <Route path="/auth/callback" element={<AppLayout><AuthCallback /></AppLayout>} />
          <Route path="/set-password" element={<AppLayout><SetPassword /></AppLayout>} />
          <Route path="/reset-password" element={<AppLayout><ResetPassword /></AppLayout>} />
          <Route path="/dashboard" element={<AppLayout><CustomerDashboard /></AppLayout>} />
          <Route path="/track/:id" element={<AppLayout><TrackOrder /></AppLayout>} />
          <Route path="/faq" element={<AppLayout><FAQ /></AppLayout>} />
          <Route path="/quartz-vs-granite-vs-marble-vs-quartzite" element={<AppLayout><MaterialComparison /></AppLayout>} />
          <Route path="/legal/:type" element={<AppLayout><Legal /></AppLayout>} />
          <Route path="/payment/success" element={<AppLayout><PaymentSuccess /></AppLayout>} />
          <Route path="/reservation/success" element={<AppLayout><ReservationSuccess /></AppLayout>} />
          <Route path="/portfolio" element={<AppLayout><Portfolio /></AppLayout>} />
          <Route path="/portfolio/:slug" element={<AppLayout><ProjectDetail /></AppLayout>} />
          <Route path="/blog" element={<AppLayout><Blog /></AppLayout>} />
          <Route path="/blog/:slug" element={<AppLayout><BlogPost /></AppLayout>} />
          <Route path="/appointments/confirm" element={<AppLayout><AppointmentConfirm /></AppLayout>} />
          <Route path="/appointments/reschedule" element={<AppLayout><AppointmentReschedule /></AppLayout>} />

          {/* Local SEO landing pages — Sarasota */}
          <Route path="/granite-countertops-sarasota" element={<AppLayout><LocalServicePage /></AppLayout>} />
          <Route path="/quartz-countertops-sarasota" element={<AppLayout><LocalServicePage /></AppLayout>} />
          <Route path="/marble-countertops-sarasota" element={<AppLayout><LocalServicePage /></AppLayout>} />
          <Route path="/quartzite-countertops-sarasota" element={<AppLayout><LocalServicePage /></AppLayout>} />
          <Route path="/countertop-installation-sarasota" element={<AppLayout><LocalServicePage /></AppLayout>} />
          {/* Local SEO landing pages — Fort Myers */}
          <Route path="/granite-countertops-fort-myers" element={<AppLayout><LocalServicePage /></AppLayout>} />
          <Route path="/quartz-countertops-fort-myers" element={<AppLayout><LocalServicePage /></AppLayout>} />
          <Route path="/countertops-fort-myers" element={<AppLayout><LocalServicePage /></AppLayout>} />
          {/* Local SEO landing pages — Naples */}
          <Route path="/granite-countertops-naples-fl" element={<AppLayout><LocalServicePage /></AppLayout>} />
          <Route path="/quartz-countertops-naples-fl" element={<AppLayout><LocalServicePage /></AppLayout>} />
          {/* Local SEO landing pages — Bradenton */}
          <Route path="/granite-countertops-bradenton" element={<AppLayout><LocalServicePage /></AppLayout>} />
          <Route path="/countertops-bradenton" element={<AppLayout><LocalServicePage /></AppLayout>} />
          {/* Local SEO landing pages — Venice */}
          <Route path="/granite-countertops-venice-fl" element={<AppLayout><LocalServicePage /></AppLayout>} />
          <Route path="/quartz-countertops-venice-fl" element={<AppLayout><LocalServicePage /></AppLayout>} />
          {/* Local SEO landing pages — Cape Coral */}
          <Route path="/granite-countertops-cape-coral" element={<AppLayout><LocalServicePage /></AppLayout>} />
          <Route path="/quartz-countertops-cape-coral" element={<AppLayout><LocalServicePage /></AppLayout>} />
          <Route path="/countertops-cape-coral" element={<AppLayout><LocalServicePage /></AppLayout>} />
          {/* Local SEO landing pages — expansion */}
          <Route path="/quartz-countertops-bradenton" element={<AppLayout><LocalServicePage /></AppLayout>} />
          <Route path="/countertops-venice-fl" element={<AppLayout><LocalServicePage /></AppLayout>} />
          <Route path="/countertops-north-port" element={<AppLayout><LocalServicePage /></AppLayout>} />
          <Route path="/countertops-naples-fl" element={<AppLayout><LocalServicePage /></AppLayout>} />
          <Route path="/granite-countertops-port-charlotte" element={<AppLayout><LocalServicePage /></AppLayout>} />
          <Route path="/countertops-punta-gorda" element={<AppLayout><LocalServicePage /></AppLayout>} />
          <Route path="/countertop-installation-fort-myers" element={<AppLayout><LocalServicePage /></AppLayout>} />

          {/* Lista de clientes compartilhada com vendedores (link com token) */}
          <Route path="/vendedores/lista" element={<SalesShareList />} />

          {/* Admin routes */}

          <Route path="/admin/*" element={
            <NewOrderDialogProvider>
              <Routes>
                <Route path="" element={<AdminLayout><AdminOverview /></AdminLayout>} />
                <Route path="settings" element={<AdminLayout><AdminSettings /></AdminLayout>} />
                <Route path="inventory" element={<AdminLayout><AdminInventory /></AdminLayout>} />
                <Route path="pricing" element={<AdminLayout><AdminPricing /></AdminLayout>} />
                <Route path="orders" element={<AdminLayout><AdminOrders /></AdminLayout>} />
                <Route path="orders/:id" element={<AdminLayout><AdminOrderDetail /></AdminLayout>} />
                <Route path="layouts" element={<AdminLayout><AdminLayouts /></AdminLayout>} />
                <Route path="layouts/:id" element={<AdminLayout><AdminLayoutDetail /></AdminLayout>} />
                <Route path="appointments" element={<AdminLayout><AdminAppointments /></AdminLayout>} />
                <Route path="availability" element={<AdminLayout><AdminAvailability /></AdminLayout>} />
                <Route path="customers" element={<AdminLayout><AdminCustomers /></AdminLayout>} />
                <Route path="leads" element={<AdminLayout><AdminLeads /></AdminLayout>} />
                <Route path="analytics" element={<AdminLayout><AdminAnalytics /></AdminLayout>} />
                <Route path="ai" element={<AdminLayout><AdminAiInsights /></AdminLayout>} />
                <Route path="seo" element={<AdminLayout><AdminSeo /></AdminLayout>} />
                <Route path="seo/indexing" element={<AdminLayout><AdminSeoIndexing /></AdminLayout>} />
                <Route path="seo/growth" element={<AdminLayout><AdminSeoGrowth /></AdminLayout>} />
                <Route path="blog" element={<AdminLayout><AdminBlog /></AdminLayout>} />
                <Route path="legal" element={<AdminLayout><AdminLegal /></AdminLayout>} />
                <Route path="financials" element={<AdminLayout><AdminFinancials /></AdminLayout>} />
                <Route path="profit-settlement" element={<AdminLayout><AdminProfitSettlement /></AdminLayout>} />
                <Route path="projects" element={<AdminLayout><AdminProjects /></AdminLayout>} />
                <Route path="permits" element={<AdminLayout><AdminPermits /></AdminLayout>} />
                <Route path="ads" element={<AdminLayout><AdminAds /></AdminLayout>} />
                <Route path="sales-list" element={<AdminLayout><AdminSalesList /></AdminLayout>} />


              </Routes>
            </NewOrderDialogProvider>
          } />

          <Route path="*" element={<NotFound />} />
        </Routes>
        </Suspense>
        <Suspense fallback={null}><CookieConsent /></Suspense>
      </BrowserRouter>
    </TooltipProvider>
    </BusinessSettingsProvider>
    </AuthProvider>
  </QueryClientProvider>
);

export default App;
